/**
 * Back-compat shim over {@link ./foyerBridge}. App-launching is now one
 * capability of the FoyerBridge; this keeps the original `launchApp` /
 * `detectPlatform` / `launchIdFor` surface (used by the schemes and tests)
 * pointing at the bridge so nothing had to change at the call sites.
 *
 * New code should import `bridge` from "./foyerBridge" and branch on
 * `bridge.capabilities()` rather than the platform.
 */
import { bridge } from "./foyerBridge";
import { flushEngagement, startDwell, trackEngagement } from "./engagement";

export {
  detectPlatform,
  launchIdFor,
  type TvPlatform,
  type LaunchableApp,
} from "./foyerBridge";

/** A launchable app that can also be attributed in engagement analytics. */
type TrackableApp = import("./foyerBridge").LaunchableApp & { id?: string };

/**
 * The app we handed the screen to, and a closure that reports how long it kept
 * it. Only ever one: a TV runs one foreground app, so a second launch without
 * an intervening return means the first session's end was never observed and
 * its duration is unknowable. We drop it rather than guess - a missing row is
 * honest, an invented one is not.
 */
let openApp: { id: string; end: () => void } | null = null;

/**
 * Close out the current app session, if there is one.
 *
 * The signal is the TV app becoming visible again, which is what happens when a
 * guest presses Home/Back out of Netflix. It is deliberately NOT `focus`: on
 * several TV platforms the launcher takes focus while still hidden behind the
 * app, which would end the session while the guest is still watching.
 */
function endAppSession(): void {
  const session = openApp;
  openApp = null;
  session?.end();
}

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") endAppSession();
  });
}

/**
 * Open `app` on the current platform. See {@link FoyerBridge.launchApp}.
 *
 * This is also the one choke point every scheme's app tile goes through, so it
 * is where a successful launch is counted for content-engagement analytics -
 * both the launch itself and, on the guest's return, how long the app held the
 * screen. "Which apps guests actually use, how often, and for how long" is the
 * question a property is buying an answer to; a launch count alone cannot tell
 * a channel someone watched all evening from one they bounced straight out of.
 *
 * We flush immediately: the very next thing that happens is the TV handing
 * focus to another application, and a buffered event would likely never be sent.
 */
export function launchApp(app: TrackableApp): boolean {
  const launched = bridge.launchApp(app);
  if (launched && app.id) {
    // A launch while one is already open means we never saw the first return;
    // discard that session rather than attribute its time to the new app.
    openApp = null;
    trackEngagement("app_launch", app.id, app.name);
    openApp = { id: app.id, end: startDwell("app_dwell", app.id, app.name) };
    flushEngagement(true);
  }
  return launched;
}
